import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import { instrumentTypes, selectors, utils, Currency } from 'state';

type Props = {
  playerId: number;
  selectedInstrumentType: string;
};

// NOTE: your original used reportingCurrency.symbol in Intl currency field (not ISO).
// Keep behavior stable by formatting as number + symbol.
const money = (n: number, reportingCurrency: Currency) =>
  `${Math.round(n).toLocaleString('en-US', { maximumFractionDigits: 0 })} ${
    reportingCurrency.symbol
  }`;

function formatMillions(v: number) {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000)
    return `${sign}${(abs / 1_000_000_000).toFixed(0)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

function truncateLabel(s: string, max = 18) {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 3)}...`;
}

const BarChart: React.FC<{
  name: string;
  points: { name: string; y: number }[];
  total: string;
  reportingCurrency: Currency;
}> = ({ name, points, total, reportingCurrency }) => {
  const categories = useMemo(() => points.map(p => p.name), [points]);
  const values = useMemo(() => points.map(p => p.y), [points]);

  const { minVal, maxVal } = useMemo(() => {
    const minV = values.length ? Math.min(...values) : 0;
    const maxV = values.length ? Math.max(...values) : 0;
    return { minVal: minV, maxVal: maxV };
  }, [values]);

  const series = useMemo(() => [{ name: ' ', data: values }], [values]);

  const options = useMemo((): ApexOptions => {
    const span = Math.max(Math.abs(minVal), Math.abs(maxVal));
    const pad = Math.max(span * 0.1, 1);
    const xMin = Math.min(0, minVal) - pad;
    const xMax = Math.max(0, maxVal) + pad;

    return {
      chart: {
        type: 'bar',
        height: 420,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      title: {
        text: `${name} PnL Distribution`.trim(),
      },
      plotOptions: {
        bar: {
          horizontal: true, // Highcharts bar
          distributed: true, // colorByPoint
          barHeight: '70%',
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },

      // Apex wants categories here even for horizontal bars (it renders them on the left)
      xaxis: {
        categories: categories.map(c => truncateLabel(c, 22)),
        min: xMin,
        max: xMax,
        tickAmount: 6,
        title: {
          text: `${reportingCurrency.symbol} value | (Total ${name} PnL: ${total})`,
        },
        labels: {
          // typed as (value: string, timestamp?) => string|string[]
          formatter: (value: string) => {
            const n = Number(value);
            return Number.isFinite(n) ? formatMillions(n) : value;
          },
        },
      },

      // Vertical grid lines only + visible zero line (matches your earlier preference)
      grid: {
        show: true,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: { left: 12, right: 12 },
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            borderColor: '#555',
            strokeDashArray: 0,
            // label: { show: false },
          },
        ],
      },

      tooltip: {
        y: {
          formatter: (val: number) => money(val, reportingCurrency),
          title: { formatter: () => '' },
        },
      },
    };
  }, [categories, minVal, maxVal, reportingCurrency, name, total]);

  return (
    <Col span={12}>
      <Chart options={options} series={series as any} type="bar" height={420} />
    </Col>
  );
};

const PnlAllocations: React.FC<Props> = ({
  playerId,
  selectedInstrumentType,
}) => {
  const { financial_instruments, session } = useSelector(
    selectors.playerById(playerId)
  );
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const reportingCurrency = utils.useReportingCurrency(session);

  const allInstruments = Object.values(financial_instruments);

  const data = useMemo(() => {
    return allInstruments.map((fn: any) => ({
      ...fn,
      underlyingAsset: underlyingAssets[fn.underlying_asset],
      pnl:
        (fn.reporting_unrealized_pnl ?? 0) + (fn.reporting_realized_pnl ?? 0),
    }));
  }, [allInstruments, underlyingAssets]);

  const grouped = useMemo(() => {
    return instrumentTypes
      .map(instrumentType => {
        const instruments = data.filter(
          (fn: any) => fn.underlyingAsset?.resourcetype === instrumentType.type
        );
        return {
          type: instrumentType.type,
          name: instrumentType.name,
          instruments,
          total: instruments.reduce(
            (acc: number, d: any) => acc + (d.pnl ?? 0),
            0
          ),
        };
      })
      .filter(gr => gr.total);
  }, [data]);

  const selectedTypes = useMemo(() => {
    if (selectedInstrumentType === 'All') return grouped;
    return grouped.filter(
      instrType => instrType.type === selectedInstrumentType
    );
  }, [grouped, selectedInstrumentType]);

  const byTypePnLDistributionChart = useMemo(() => {
    return selectedTypes.map(group => {
      const seriesData = group.instruments;

      if (seriesData.length > 10) {
        const sorted = [...group.instruments].sort((a: any, b: any) =>
          Math.abs(a.pnl) > Math.abs(b.pnl) ? 1 : -1
        );

        const top10 = sorted.slice(-10);
        const trailing = sorted.slice(0, sorted.length - 10);

        const othersTickers = trailing
          .map(
            (instrument: any) =>
              underlyingAssets[instrument.underlying_asset]?.ticker
          )
          .filter(Boolean);

        const others = {
          name: `others: ${othersTickers.join(', ')}`,
          y: Math.round(
            trailing.reduce((acc: number, d: any) => acc + (d.pnl ?? 0), 0)
          ),
        };

        const points: { name: string; y: number }[] = top10.map(
          (instrument: any) => ({
            name: instrument.underlyingAsset?.ticker ?? 'Unknown',
            y: Math.round(instrument.pnl ?? 0),
          })
        );
        points.push(others);

        return (
          <BarChart
            key={`type-${group.type}`}
            name={group.name}
            points={points}
            total={money(group.total, reportingCurrency)}
            reportingCurrency={reportingCurrency}
          />
        );
      }

      const points: { name: string; y: number }[] = seriesData.map(
        (instrument: any) => ({
          name: instrument.underlyingAsset?.ticker ?? 'Unknown',
          y: Math.round(instrument.pnl ?? 0),
        })
      );

      return (
        <BarChart
          key={`type-${group.type}`}
          name={group.name}
          points={points}
          total={money(group.total, reportingCurrency)}
          reportingCurrency={reportingCurrency}
        />
      );
    });
  }, [selectedTypes, underlyingAssets, reportingCurrency]);

  const generalPnLDistributionChart = useMemo(() => {
    // Combine legacy and class-19 resource rows under their shared user-facing Options label.
    const totalsByName = grouped.reduce<Record<string, number>>((totals, gr) => {
      totals[gr.name] = (totals[gr.name] || 0) + gr.total;
      return totals;
    }, {});
    const distributionSeries = Object.entries(totalsByName).map(
      ([name, total]) => ({ name, y: Math.round(total) })
    );
    const total = grouped.reduce((acc, gr) => acc + gr.total, 0);

    return (
      <BarChart
        key="general"
        name=" "
        points={distributionSeries}
        total={money(total, reportingCurrency)}
        reportingCurrency={reportingCurrency}
      />
    );
  }, [grouped, reportingCurrency]);

  return (
    <Row>
      {generalPnLDistributionChart}
      {byTypePnLDistributionChart}
    </Row>
  );
};

export default PnlAllocations;
